import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import { useTranslation } from 'react-i18next';
import { selectLocale } from 'models/localization';
import InsertInvitationIcon from 'components/icons/Calendar';
import EmailIcon from 'components/icons/EmailSmall';
import ChatroomIcon from 'components/icons/Chatroom';
import {
  DEFAULT_THEME_COLOR,
} from 'appenv';
import VideoCall from 'components/icons/VideoCall';
import Livestream from 'components/icons/Livestream';
import { BoothContext } from 'pages/booths/BoothProfileContainer';
import { TabsContext } from 'components/TabsContext';
import { useSelector } from 'react-redux';
import isGroupVideoCallDisabled from 'components/chat/utils/isGroupVideoCallDisabled';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import useJoinGroupChatSwitch from 'hooks/useJoinGroupChatSwitch';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import useEncodedMeetupUrl from 'hooks/useEncodedMeetupUrl';

const Container = styled.div`
  min-height: 120px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  text-align: center;
`;

const TitleContainer = styled.div`
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 16px;
`;

const BtnGroupContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  max-width: 360px;
  flex-wrap: wrap;
  margin-bottom: -32px;
`;

const StyledHyperLink = styled.a`
  text-decoration: none;
  color: ${({ color }) => color};

  &:visited {
    color: ${({ color }) => color};
  }
`;

const BtnContainer = styled.div`
  margin-bottom: 32px;
  flex: 0 0 auto;
  min-width: 80px;
  max-width: 120px;
  cursor: pointer;
  color: ${({ color }) => color} !important;

  & svg {
    fill: ${({ color }) => color};
  }
`;

const BusinessPopover = ({
  boothId,
  boothName,
  emailContacts,
  meetupUrl,
  livestreams,
  videoCallUrl,
  className,
}) => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const encodedMeetupUrl = useEncodedMeetupUrl(meetupUrl);
  const boothForMatomo = useContext(BoothContext);
  const {
    themeColor, features, hall,
    chatroomConfig,
  } = useSelector((s) => s.eventConfigs);
  const resolvedColor = themeColor || DEFAULT_THEME_COLOR;
  const mailToLink = useMemo(() => {
    if (emailContacts && emailContacts.length > 0) {
      const email = emailContacts.find((eachemailContact) => (eachemailContact.type === 'email'));
      if (email?.contact) {
        return `mailto:${email.contact}`;
      }
    }
    return '';
  }, [emailContacts]);

  const { joinBoothChat } = useJoinGroupChatSwitch();

  const openChatWindow = () => {
    window.trackingItemToFirebase(null, 'Booth', 'Chat', 'Group_Chat_Click', 'Booth_Profile', boothId, boothForMatomo.boothNameForMatomo);
    const isAutoJoinMsgDisabled = chatroomConfig?.isAutoJoinMsgDisabled;
    joinBoothChat(boothId, boothName, !isAutoJoinMsgDisabled);
  };

  const { setActiveTabKey } = useContext(TabsContext);

  const goLiveStreamTab = (index) => {
    setActiveTabKey(index);
    document.getElementById('booth-container')
      .scroll({
        top: 1000,
        left: 0,
        behavior: 'smooth',
      });
  };

  const clickMeetup = () => {
    window.trackingItemToFirebase(null, 'Booth', 'Meetup', 'Meetup_Click', 'Booth_Profile', boothId, boothForMatomo.boothNameForMatomo);
  };

  const clickLeaveMessage = () => {
    window.trackingItemToFirebase(null, 'Booth', 'Leave a Message', 'Leave a Message_Click', 'Booth_Profile', boothId, boothForMatomo.boothNameForMatomo);
  };

  const boothGreeting = useMemo(() => findAvailableLocale(hall?.popOver?.boothGreeting, locale) || t('booth.greeting', 'Hi, feel free to talk to us!'), [t, locale]);
  const chatMessage = useMemo(() => findAvailableLocale(hall?.popOver?.chat, locale) || t('booth.chat', 'Chat'), [t, locale]);
  const mailToMessage = useMemo(() => findAvailableLocale(hall?.popOver?.mailTo, locale) || t('booth.leave_a_message', 'Leave a Message'), [t, locale]);
  const meetupMessage = useMemo(() => findAvailableLocale(hall?.popOver?.meetup, locale) || t('booth.meetup', 'Meetup'), [t, locale]);
  const videoCallMessage = useMemo(() => findAvailableLocale(hall?.popOver?.videoCall, locale) || t('booth.video_call', 'Video Call'), [t, locale]);
  const livestreamMessage = useMemo(() => findAvailableLocale(hall?.popOver?.livestream, locale) || t('booth.live_stream', 'Livestream'), [t, locale]);

  return (
    <Paper elevation={3} className={className}>
      <Container>
        <TitleContainer>
          {boothGreeting}
        </TitleContainer>
        <BtnGroupContainer>
          {
            !isChatroomDisabled(chatroomConfig) && (
              <BtnContainer onClick={openChatWindow} color={resolvedColor}>
                <ChatroomIcon width={24} height={22} color={resolvedColor} />
                <div>
                  {chatMessage}
                </div>
              </BtnContainer>
            )
          }
          {
            mailToLink && (
              <StyledHyperLink href={mailToLink} target="_blank" rel="noopener noreferrer" color={resolvedColor}>
                <BtnContainer onClick={clickLeaveMessage} color={resolvedColor}>
                  <EmailIcon width={24} height={22} color={resolvedColor} />
                  <div>{mailToMessage}</div>
                </BtnContainer>
              </StyledHyperLink>
            )
          }
          {
            meetupUrl && (
              <StyledHyperLink href={encodedMeetupUrl} target="_blank" rel="noopener noreferrer" color={resolvedColor}>
                <BtnContainer onClick={clickMeetup} color={resolvedColor}>
                  <InsertInvitationIcon width={24} height={22} color={resolvedColor} />
                  <div>{meetupMessage}</div>
                </BtnContainer>
              </StyledHyperLink>
            )
          }
          {
            features.includes('groupVideoCall') && !isGroupVideoCallDisabled(chatroomConfig)
              ? (
                <BtnContainer color={resolvedColor}>
                  <VideoCall width={24} height={22} color={resolvedColor} />
                  <div>{videoCallMessage}</div>
                </BtnContainer>
              ) : (
                <>
                  {
                    videoCallUrl && !isGroupVideoCallDisabled() && (
                      <StyledHyperLink href={videoCallUrl} target="_blank" rel="noopener noreferrer">
                        <BtnContainer>
                          <VideoCall width={24} height={22} color={resolvedColor} />
                          <div>{videoCallMessage}</div>
                        </BtnContainer>
                      </StyledHyperLink>
                    )
                  }
                </>
              )
          }
          {
            livestreams && livestreams.length > 0 && (
              <BtnContainer onClick={() => goLiveStreamTab(livestreams[0].index)} color={resolvedColor}>
                <Livestream width={24} height={22} color={resolvedColor} />
                <div>{livestreamMessage}</div>
              </BtnContainer>
            )
          }
        </BtnGroupContainer>
      </Container>
    </Paper>
  );
};

BusinessPopover.propTypes = {
  boothId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  boothName: PropTypes.string.isRequired,
  emailContacts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  meetupUrl: PropTypes.string,
  livestreams: PropTypes.arrayOf(PropTypes.shape()),
  videoCallUrl: PropTypes.string,
  className: PropTypes.string,
};

BusinessPopover.defaultProps = {
  meetupUrl: '',
  livestreams: [],
  videoCallUrl: '',
  className: undefined,
};

export default BusinessPopover;
